import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn, ColumnCenter } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import { AutoRow, RowBetween } from 'components/Row'
import { Dots } from 'components/swap/styleds'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { useConnection } from 'hooks/useConnection'
import { useTokenAllowance } from 'hooks/useTokenAllowance'
import { useCallback, useContext, useEffect, useState } from 'react'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'

import { useStepLineState, useStepLineStateUpdate } from '../context/StepLineContext'
import Step from './Step'

export const useApproveStep = (
  amount?: CurrencyAmount<Currency>,
  spender?: string,
  hideCurrencyLogo = false,
  useExactApproval = false
): Step => {
  const [approvalState, approve] = useApproveCallback(amount, spender, useExactApproval)
  const token = amount?.currency?.isToken ? amount.currency : undefined
  const approvalKey = `__approval_completed/${token?.address || 'unknown'}`

  const state = useStepLineState()
  const updateState = useStepLineStateUpdate()
  const completedInSession = state[approvalKey] || false

  // Lock completion when approved (this is already stored in context, so auto-cleared on modal close)
  useEffect(() => {
    if (approvalState === ApprovalState.APPROVED && !completedInSession) {
      updateState({ [approvalKey]: true })
    }
  }, [approvalState, completedInSession, approvalKey, updateState])

  return {
    condition: completedInSession || approvalState === ApprovalState.APPROVED,
    component: (
      <ApproveStep
        approve={approve}
        amount={amount}
        approvalState={approvalState}
        spender={spender}
        hideCurrencyLogo={hideCurrencyLogo}
        useExactApproval={useExactApproval}
      />
    ),
  }
}

type ApproveStepProps = {
  approve: () => Promise<void>
  approvalState: ApprovalState
  amount?: CurrencyAmount<Currency>
  spender?: string
  hideCurrencyLogo?: boolean
  useExactApproval?: boolean
}
const ApproveStep = ({
  approve,
  approvalState,
  amount,
  spender,
  hideCurrencyLogo = false,
  useExactApproval = false,
}: ApproveStepProps) => {
  const theme = useContext(ThemeContext)
  const { account } = useConnection()
  const token = amount?.currency?.isToken ? amount.currency : undefined
  const currentAllowance = useTokenAllowance(token, account ?? undefined, spender)

  const [approvalSubmitted, setApprovalSubmitted] = useState(false)

  useEffect(() => {
    setApprovalSubmitted(false)
  }, [token?.address])

  const handleApprove = useCallback(async () => {
    if (approvalState !== ApprovalState.NOT_APPROVED) return

    // Set submitted flag before calling approve
    setApprovalSubmitted(true)

    try {
      await approve()
    } catch (error) {
      console.error('Approval failed:', error)
      // Reset submitted flag if approval fails
      setApprovalSubmitted(false)
    }
  }, [approve, approvalState])

  const buttonDisabled =
    approvalState === ApprovalState.APPROVED ||
    approvalSubmitted ||
    approvalState === ApprovalState.PENDING ||
    approvalState === ApprovalState.UNKNOWN

  return (
    <ColumnCenter gap="8px">
      <LightCard>
        <AutoColumn gap="sm">
          <RowBetween>
            <AutoRow>
              <div>{amount?.toExact()}</div>
            </AutoRow>

            <AutoRow gap="2px" justify="flex-end">
              {!hideCurrencyLogo && <CurrencyLogo currency={amount?.currency}></CurrencyLogo>}
              <div>{amount?.currency.symbol}</div>
            </AutoRow>
          </RowBetween>
          <Separator />
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              Current allowance
            </ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              {currentAllowance?.toExact()} {amount?.currency.symbol}
            </ThemedText.DeprecatedSubHeader>
          </RowBetween>
          <RowBetween>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              Requested allowance
            </ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedSubHeader color={theme.textSecondary}>
              {useExactApproval ? amount?.toSignificant(6) : '∞'}
            </ThemedText.DeprecatedSubHeader>
          </RowBetween>
        </AutoColumn>
      </LightCard>
      <ButtonPrimary onClick={handleApprove} disabled={buttonDisabled}>
        {approvalState === ApprovalState.UNKNOWN ? (
          <ThemedText.DeprecatedSubHeader>
            Checking existing allowance
            <Dots />
          </ThemedText.DeprecatedSubHeader>
        ) : (
          <ThemedText.DeprecatedSubHeader>
            Approve {amount?.currency.symbol}
            {approvalState === ApprovalState.PENDING && <Dots />}
          </ThemedText.DeprecatedSubHeader>
        )}
      </ButtonPrimary>
    </ColumnCenter>
  )
}

export default ApproveStep
